import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { Trash2, Plus, Image as ImageIcon, Video, Lock, Upload, Loader2, Settings, Eye, EyeOff, ExternalLink, Shield, QrCode, Monitor, Smartphone, RefreshCw, Activity, Clock, Download, Upload as UploadIcon, Play, Pause, Wifi, WifiOff, Mail, Send, Cloud, Edit2, Check, X, Minus, Tv, Sun, Moon, RotateCcw, Globe, Ticket, MessageSquare } from 'lucide-react';
import axios from 'axios';
import WhatsNew from '../components/WhatsNew';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper function to convert YouTube URLs to embed format
const getYouTubeEmbedUrl = (url) => {
  if (!url) return url;
  if (url.includes('/embed/')) return url;
  
  let videoId = null;
  const watchMatch = url.match(/[?&]v=([^&]+)/);
  if (watchMatch) videoId = watchMatch[1];
  const shortMatch = url.match(/youtu\.be\/([^?&]+)/);
  if (shortMatch) videoId = shortMatch[1];
  
  if (videoId) return `https://www.youtube.com/embed/${videoId}`;
  return url;
};

const MasterAdmin = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [setupRequired, setSetupRequired] = useState(false);
  const [qrCode, setQrCode] = useState('');
  const [secretKey, setSecretKey] = useState('');
  const [loginStep, setLoginStep] = useState('password'); // 'password', 'setup', 'totp'
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);
  const [selectedPage, setSelectedPage] = useState(null);
  const [newItemUrl, setNewItemUrl] = useState('');
  const [newItemType, setNewItemType] = useState('image');
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('content'); // 'content', 'playlists', 'health', 'backup'
  const [displayHealth, setDisplayHealth] = useState([]);
  const [healthLoading, setHealthLoading] = useState(false);
  const [refreshing, setRefreshing] = useState({});
  
  // Playlist state
  const [editingPlaylist, setEditingPlaylist] = useState(null);
  const [playlistName, setPlaylistName] = useState('');
  const [playlistStartHour, setPlaylistStartHour] = useState(0);
  const [playlistEndHour, setPlaylistEndHour] = useState(24);
  
  // Update notification state
  const [sendingNotification, setSendingNotification] = useState(false);
  const [notificationChanges, setNotificationChanges] = useState('');
  
  // Display naming state
  const [editingDisplayId, setEditingDisplayId] = useState(null);
  const [editDisplayName, setEditDisplayName] = useState('');
  
  // Ticket management state
  const [tickets, setTickets] = useState([]);
  const [ticketsLoading, setTicketsLoading] = useState(false);
  const [editingTicketId, setEditingTicketId] = useState(null);
  const [editTicketStatus, setEditTicketStatus] = useState('');
  const [editTicketNotes, setEditTicketNotes] = useState('');
  const [editTicketModel, setEditTicketModel] = useState('');
  const [editTicketSerial, setEditTicketSerial] = useState('');
  
  // Fully Kiosk state
  const [fullyPassword, setFullyPassword] = useState('');
  const [fullyEnabled, setFullyEnabled] = useState(true);
  const [fullySending, setFullySending] = useState({});
  
  // Create new page state
  const [showCreatePage, setShowCreatePage] = useState(false);
  const [newPageId, setNewPageId] = useState('');
  const [newPageName, setNewPageName] = useState('');
  const [newPagePassword, setNewPagePassword] = useState('');
  const [creatingPage, setCreatingPage] = useState(false);
  
  // Weather zip code state
  const [weatherZipInput, setWeatherZipInput] = useState('');
  
  // Slide duration state
  const [slideDurationInput, setSlideDurationInput] = useState(5);
  
  // Show default ad state  
  const [showDefaultAdInput, setShowDefaultAdInput] = useState(true);
  
  useEffect(() => {
    const auth = sessionStorage.getItem('masterAdminAuth');
    if (auth === 'true') {
      setIsAuthenticated(true);
      loadPages();
    } else {
      checkTotpStatus();
    }
  }, []);
  
  const checkTotpStatus = async () => {
    try {
      const response = await axios.get(`${API}/admin/totp-status`);
      setSetupRequired(response.data.setup_required);
    } catch (error) {
      console.error('Failed to check TOTP status:', error);
    }
  };
  
  const loadPages = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/pages`);
      setPages(response.data);
      if (response.data.length > 0 && !selectedPage) {
        setSelectedPage(response.data[0]);
      }
    } catch (error) {
      console.error('Failed to load pages:', error);
      toast.error('Failed to load pages');
    } finally {
      setLoading(false);
    }
  };
  
  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    
    try {
      const response = await axios.post(`${API}/admin/master-login`, {
        password
      });
      
      if (response.data.requires_otp) {
        // SMS OTP sent, move to OTP step
        setLoginStep('totp');
        toast.success('Verification code sent to your phone');
      } else if (response.data.success) {
        setIsAuthenticated(true);
        sessionStorage.setItem('masterAdminAuth', 'true');
        loadPages();
        toast.success('Login successful!');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid password');
      setPassword('');
    } finally {
      setLoginLoading(false);
    }
  };
  
  const handleTotpSubmit = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    
    try {
      const response = await axios.post(`${API}/admin/master-login`, {
        password,
        totp_code: totpCode
      });
      
      if (response.data.success) {
        setIsAuthenticated(true);
        sessionStorage.setItem('masterAdminAuth', 'true');
        loadPages();
        toast.success('Login successful!');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid code');
      setTotpCode('');
    } finally {
      setLoginLoading(false);
    }
  };
  
  const handleSetupComplete = async () => {
    if (!totpCode || totpCode.length !== 6) {
      toast.error('Please enter the 6-digit code from your authenticator app');
      return;
    }
    
    setLoginLoading(true);
    try {
      const response = await axios.post(`${API}/admin/master-login`, {
        password,
        totp_code: totpCode
      });
      
      if (response.data.success) {
        setIsAuthenticated(true);
        sessionStorage.setItem('masterAdminAuth', 'true');
        setSetupRequired(false);
        loadPages();
        toast.success('2FA setup complete! Login successful.');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid code. Make sure you scanned the QR code correctly.');
      setTotpCode('');
    } finally {
      setLoginLoading(false);
    }
  };
  
  const handleLogout = () => {
    setIsAuthenticated(false);
    sessionStorage.removeItem('masterAdminAuth');
    setPassword('');
    setTotpCode('');
    setLoginStep('password');
    setSelectedPage(null);
    setQrCode('');
    setSecretKey('');
    toast.success('Logged out');
  };
  
  const togglePageEnabled = async (pageId, enabled) => {
    try {
      await axios.put(`${API}/admin/pages/${pageId}`, { enabled });
      setPages(pages.map(p => p.id === pageId ? { ...p, enabled } : p));
      if (selectedPage?.id === pageId) {
        setSelectedPage({ ...selectedPage, enabled });
      }
      toast.success(`Page ${enabled ? 'enabled' : 'disabled'}`);
    } catch (error) {
      toast.error('Failed to update page');
    }
  };
  
  const toggleOrientation = async (pageId, orientation) => {
    try {
      await axios.put(`${API}/admin/pages/${pageId}`, { orientation });
      setPages(pages.map(p => p.id === pageId ? { ...p, orientation } : p));
      if (selectedPage?.id === pageId) {
        setSelectedPage({ ...selectedPage, orientation });
      }
      toast.success(`Orientation set to ${orientation}`);
    } catch (error) {
      toast.error('Failed to update orientation');
    }
  };
  
  const savePageContent = async (pageId, items) => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/pages/${pageId}`, { items });
      setPages(pages.map(p => p.id === pageId ? { ...p, items } : p));
      if (selectedPage?.id === pageId) {
        setSelectedPage({ ...selectedPage, items });
      }
      toast.success('Content saved!');
    } catch (error) {
      toast.error('Failed to save content');
    } finally {
      setSaving(false);
    }
  };
  
  const handleAddItem = () => {
    if (!newItemUrl.trim() || !selectedPage) return;
    
    const processedUrl = newItemType === 'video' 
      ? getYouTubeEmbedUrl(newItemUrl.trim())
      : newItemUrl.trim();
    
    const newItems = [...(selectedPage.items || []), { type: newItemType, url: processedUrl }];
    savePageContent(selectedPage.id, newItems);
    setNewItemUrl('');
  };
  
  const handleRemoveItem = (index) => {
    if (!selectedPage) return;
    const newItems = selectedPage.items.filter((_, i) => i !== index);
    savePageContent(selectedPage.id, newItems);
  };
  
  const handleUpdateItemDescription = async (index, description) => {
    if (!selectedPage) return;
    try {
      await axios.put(`${API}/admin/pages/${selectedPage.id}`, { items: selectedPage.items });
      setPages(pages.map(p => p.id === selectedPage.id ? { ...p, items: selectedPage.items } : p));
    } catch (error) {
      console.error('Failed to save description:', error);
    }
  };
  
  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedPage) return;
    
    if (file.size > 1024 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 1GB');
      return;
    }
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      const fileUrl = `${BACKEND_URL}${response.data.url}`;
      const newItems = [...(selectedPage.items || []), { type: response.data.type, url: fileUrl }];
      savePageContent(selectedPage.id, newItems);
      toast.success('File uploaded!');
    } catch (error) {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };
  
  const updatePagePassword = async (pageId, newPassword) => {
    try {
      await axios.put(`${API}/admin/pages/${pageId}`, { password: newPassword });
      setPages(pages.map(p => p.id === pageId ? { ...p, password: newPassword } : p));
      if (selectedPage?.id === pageId) {
        setSelectedPage({ ...selectedPage, password: newPassword });
      }
      toast.success('Password updated!');
    } catch (error) {
      toast.error('Failed to update password');
    }
  };
  
  const updateWeatherZipCode = async (pageId, zipCode) => {
    try {
      await axios.put(`${API}/admin/pages/${pageId}`, { weather_zip_code: zipCode || '' });
      setPages(pages.map(p => p.id === pageId ? { ...p, weather_zip_code: zipCode } : p));
      if (selectedPage?.id === pageId) {
        setSelectedPage({ ...selectedPage, weather_zip_code: zipCode });
      }
      toast.success(zipCode ? `Weather zip code set to ${zipCode}` : 'Weather set to auto-detect');
    } catch (error) {
      toast.error('Failed to update weather zip code');
    }
  };
  
  const updateSlideDuration = async (pageId, duration) => {
    const val = Math.max(3, Math.min(120, duration));
    setSlideDurationInput(val);
    try {
      await axios.put(`${API}/admin/pages/${pageId}`, { slide_duration: val });
      setPages(pages.map(p => p.id === pageId ? { ...p, slide_duration: val } : p));
      if (selectedPage?.id === pageId) {
        setSelectedPage({ ...selectedPage, slide_duration: val });
      }
      toast.success(`Slide duration set to ${val} seconds`);
    } catch (error) {
      toast.error('Failed to update slide duration');
    }
  };
  
  const toggleDefaultAd = async (pageId, enabled) => {
    setShowDefaultAdInput(enabled);
    try {
      await axios.put(`${API}/admin/pages/${pageId}`, { show_default_ad: enabled });
      setPages(pages.map(p => p.id === pageId ? { ...p, show_default_ad: enabled } : p));
      if (selectedPage?.id === pageId) {
        setSelectedPage({ ...selectedPage, show_default_ad: enabled });
      }
      toast.success(enabled ? 'FrostByte ad enabled' : 'FrostByte ad hidden');
    } catch (error) {
      setShowDefaultAdInput(!enabled);
      toast.error('Failed to update ad setting');
    }
  };
  
  // ==================== REMOTE REFRESH FUNCTIONS ====================
  const handleRefreshPage = async (pageId) => {
    setRefreshing(prev => ({ ...prev, [pageId]: true }));
    try {
      await axios.post(`${API}/admin/pages/${pageId}/refresh`);
      toast.success(`Refresh command sent to ${pageId}`);
    } catch (error) {
      toast.error('Failed to send refresh command');
    } finally {
      setRefreshing(prev => ({ ...prev, [pageId]: false }));
    }
  };
  
  const handleRefreshAll = async () => {
    setRefreshing(prev => ({ ...prev, all: true }));
    try {
      await axios.post(`${API}/admin/refresh-all`);
      toast.success('Refresh command sent to all displays');
    } catch (error) {
      toast.error('Failed to send refresh command');
    } finally {
      setRefreshing(prev => ({ ...prev, all: false }));
    }
  };
  
  // ==================== HEALTH MONITORING FUNCTIONS ====================
  const loadDisplayHealth = async () => {
    setHealthLoading(true);
    try {
      const response = await axios.get(`${API}/health/displays`);
      setDisplayHealth(response.data);
    } catch (error) {
      console.error('Failed to load display health:', error);
    } finally {
      setHealthLoading(false);
    }
  };
  
  const removeDisplay = async (displayId) => {
    try {
      await axios.delete(`${API}/health/displays/${displayId}`);
      setDisplayHealth(displayHealth.filter(d => d.display_id !== displayId));
      toast.success('Display removed');
    } catch (error) {
      toast.error('Failed to remove display');
    }
  };
  
  const startEditingDisplayName = (display) => {
    setEditingDisplayId(display.display_id);
    setEditDisplayName(display.display_name || '');
  };
  
  const saveDisplayName = async (displayId) => {
    try {
      await axios.put(`${API}/health/displays/${displayId}/name`, {
        display_name: editDisplayName
      });
      setDisplayHealth(displayHealth.map(d => 
        d.display_id === displayId ? { ...d, display_name: editDisplayName } : d
      ));
      toast.success('Display name updated');
    } catch (error) {
      toast.error('Failed to update display name');
    } finally {
      setEditingDisplayId(null);
      setEditDisplayName('');
    }
  };

  // Fully Kiosk functions
  const loadFullySettings = async () => {
    try {
      const response = await axios.get(`${API}/fully/settings`);
      setFullyPassword(response.data.password || '');
      setFullyEnabled(response.data.enabled !== false);
    } catch (error) {
      console.error('Failed to load Fully settings:', error);
    }
  };

  const saveFullySettings = async () => {
    try {
      await axios.put(`${API}/fully/settings?password=${encodeURIComponent(fullyPassword)}&enabled=${fullyEnabled}`);
      toast.success('Fully Kiosk settings saved');
    } catch (error) {
      toast.error('Failed to save Fully settings');
    }
  };

  const sendFullyCommand = async (displayId, command, params = {}) => {
    setFullySending(prev => ({ ...prev, [`${displayId}-${command}`]: true }));
    try {
      await axios.post(`${API}/fully/command`, { display_id: displayId, command, params });
      toast.success(`Command "${command}" sent`);
    } catch (error) {
      toast.error(`Failed to send command`);
    } finally {
      setFullySending(prev => ({ ...prev, [`${displayId}-${command}`]: false }));
    }
  };

  const sendFullyBulkCommand = async (command) => {
    setFullySending(prev => ({ ...prev, [`bulk-${command}`]: true }));
    try {
      await axios.post(`${API}/fully/command/bulk?command=${command}`);
      toast.success(`Command "${command}" sent to all displays`);
    } catch (error) {
      toast.error(`Failed to send bulk command`);
    } finally {
      setFullySending(prev => ({ ...prev, [`bulk-${command}`]: false }));
    }
  };
  
  // ==================== PLAYLIST FUNCTIONS ====================
  const toggleUsePlaylists = async (pageId, enabled) => {
    try {
      await axios.put(`${API}/admin/pages/${pageId}/use-playlists?enabled=${enabled}`);
      setPages(pages.map(p => p.id === pageId ? { ...p, use_playlists: enabled } : p));
      if (selectedPage?.id === pageId) {
        setSelectedPage({ ...selectedPage, use_playlists: enabled });
      }
      toast.success(enabled ? 'Playlist mode enabled' : 'Playlist mode disabled');
    } catch (error) {
      toast.error('Failed to update playlist mode');
    }
  };
  
  const savePlaylist = async () => {
    if (!selectedPage || !playlistName.trim()) return;
    
    const newPlaylist = {
      name: playlistName.trim(),
      start_hour: parseInt(playlistStartHour),
      end_hour: parseInt(playlistEndHour),
      items: editingPlaylist?.items || []
    };
    
    let updatedPlaylists;
    if (editingPlaylist && editingPlaylist.index !== undefined) {
      // Editing existing playlist
      updatedPlaylists = [...(selectedPage.playlists || [])];
      updatedPlaylists[editingPlaylist.index] = newPlaylist;
    } else {
      // Adding new playlist
      updatedPlaylists = [...(selectedPage.playlists || []), newPlaylist];
    }
    
    try {
      await axios.put(`${API}/admin/pages/${selectedPage.id}/playlists`, { playlists: updatedPlaylists });
      setPages(pages.map(p => p.id === selectedPage.id ? { ...p, playlists: updatedPlaylists } : p));
      setSelectedPage({ ...selectedPage, playlists: updatedPlaylists });
      setEditingPlaylist(null);
      setPlaylistName('');
      setPlaylistStartHour(0);
      setPlaylistEndHour(24);
      toast.success('Playlist saved!');
    } catch (error) {
      toast.error('Failed to save playlist');
    }
  };
  
  const deletePlaylist = async (index) => {
    if (!selectedPage) return;
    
    const updatedPlaylists = selectedPage.playlists.filter((_, i) => i !== index);
    
    try {
      await axios.put(`${API}/admin/pages/${selectedPage.id}/playlists`, { playlists: updatedPlaylists });
      setPages(pages.map(p => p.id === selectedPage.id ? { ...p, playlists: updatedPlaylists } : p));
      setSelectedPage({ ...selectedPage, playlists: updatedPlaylists });
      toast.success('Playlist deleted');
    } catch (error) {
      toast.error('Failed to delete playlist');
    }
  };
  
  const startEditPlaylist = (playlist, index) => {
    setEditingPlaylist({ ...playlist, index });
    setPlaylistName(playlist.name);
    setPlaylistStartHour(playlist.start_hour);
    setPlaylistEndHour(playlist.end_hour);
  };
  
  // ==================== BACKUP FUNCTIONS ====================
  const handleExportBackup = async () => {
    try {
      const response = await axios.get(`${API}/admin/backup/full`);
      const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `frostbyte-full-backup-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('Full database backup exported successfully');
    } catch (error) {
      toast.error('Failed to export backup');
    }
  };
  
  const handleImportBackup = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    try {
      const text = await file.text();
      const data = JSON.parse(text);
      
      // Support both v1 (pages-only) and v2 (full backup) formats
      if (data.version === '2.0' && data.type === 'full_backup') {
        await axios.post(`${API}/admin/backup/full/import`, data);
        toast.success('Full database backup imported successfully');
      } else if (data.pages && Array.isArray(data.pages)) {
        await axios.post(`${API}/admin/backup/import`, { pages: data.pages });
        toast.success('Backup imported successfully');
      } else {
        toast.error('Invalid backup file format');
        return;
      }
      
      loadPages();
    } catch (error) {
      toast.error('Failed to import backup: ' + (error.response?.data?.detail || error.message));
    } finally {
      e.target.value = '';
    }
  };
  
  // Load health when tab changes
  useEffect(() => {
    if (activeTab === 'health' && isAuthenticated) {
      loadDisplayHealth();
      loadFullySettings();
      const interval = setInterval(loadDisplayHealth, 30000);
      return () => clearInterval(interval);
    }
  }, [activeTab, isAuthenticated]);

  // Load tickets when tab changes
  useEffect(() => {
    if (activeTab === 'tickets' && isAuthenticated) {
      loadTickets();
    }
  }, [activeTab, isAuthenticated]);

  
  // Send update notification
  const handleSendNotification = async () => {
    if (!notificationChanges.trim()) {
      toast.error('Please enter the changes for this update');
      return;
    }
    
    setSendingNotification(true);
    try {
      const changes = notificationChanges.split('\n').filter(c => c.trim());
      await axios.post(`${API}/admin/send-update-notification`, {
        version: 'Snowman 1',
        changes: changes
      });
      toast.success('Update notification sent to stakeholders!');
      setNotificationChanges('');
    } catch (error) {
      toast.error('Failed to send notification: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSendingNotification(false);
    }
  };

  // ==================== TICKET MANAGEMENT FUNCTIONS ====================
  const loadTickets = async () => {
    setTicketsLoading(true);
    try {
      const response = await axios.get(`${API}/tickets/admin/all`);
      setTickets(response.data.tickets || []);
    } catch (error) {
      console.error('Failed to load tickets:', error);
      toast.error('Failed to load tickets');
    } finally {
      setTicketsLoading(false);
    }
  };

  const startEditTicket = (ticket) => {
    setEditingTicketId(ticket.ticket_id);
    setEditTicketStatus(ticket.status);
    setEditTicketNotes(ticket.admin_notes || '');
    setEditTicketModel(ticket.device_model || '');
    setEditTicketSerial(ticket.serial_number || '');
  };

  const cancelEditTicket = () => {
    setEditingTicketId(null);
    setEditTicketStatus('');
    setEditTicketNotes('');
    setEditTicketModel('');
    setEditTicketSerial('');
  };

  const saveTicketUpdate = async (ticketId) => {
    try {
      await axios.put(`${API}/tickets/admin/update/${ticketId}`, {
        status: editTicketStatus,
        admin_notes: editTicketNotes,
        device_model: editTicketModel,
        serial_number: editTicketSerial
      });
      setTickets(tickets.map(t => t.ticket_id === ticketId 
        ? { ...t, status: editTicketStatus, admin_notes: editTicketNotes, device_model: editTicketModel, serial_number: editTicketSerial, updated_at: new Date().toISOString() }
        : t
      ));
      cancelEditTicket();
      toast.success('Ticket updated');
    } catch (error) {
      toast.error('Failed to update ticket');
    }
  };


  
  // Create new page
  const handleCreatePage = async () => {
    if (!newPageId.trim()) {
      toast.error('Please enter a page ID');
      return;
    }
    if (!newPageName.trim()) {
      toast.error('Please enter a page name');
      return;
    }
    if (!newPagePassword.trim()) {
      toast.error('Please enter a password');
      return;
    }
    
    // Validate page ID format (lowercase, alphanumeric, hyphens allowed)
    const pageIdRegex = /^[a-z0-9-]+$/;
    if (!pageIdRegex.test(newPageId)) {
      toast.error('Page ID must be lowercase letters, numbers, and hyphens only');
      return;
    }
    
    setCreatingPage(true);
    try {
      const response = await axios.post(`${API}/admin/pages`, {
        id: newPageId,
        name: newPageName,
        password: newPagePassword,
        enabled: true,
        orientation: 'landscape',
        route: `/page/${newPageId}`,
        admin_route: `/page/${newPageId}/admin`
      });
      
      toast.success(`Page "${newPageName}" created successfully!`);
      setShowCreatePage(false);
      setNewPageId('');
      setNewPageName('');
      setNewPagePassword('');
      loadPages();
      setSelectedPage(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create page');
    } finally {
      setCreatingPage(false);
    }
  };
  
  // Delete page
  const handleDeletePage = async (pageId) => {
    if (!window.confirm(`Are you sure you want to delete the page "${pageId}"? This cannot be undone.`)) {
      return;
    }
    
    try {
      await axios.delete(`${API}/admin/pages/${pageId}`);
      toast.success('Page deleted');
      loadPages();
      if (selectedPage?.id === pageId) {
        setSelectedPage(null);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete page');
    }
  };
  
  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-slate-900 border-slate-800">
          <CardContent className="pt-6">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-cyan-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="w-8 h-8 text-cyan-400" />
              </div>
              <h1 className="text-2xl font-bold text-white">Master Admin Panel</h1>
              <p className="text-slate-400 mt-2">
                {loginStep === 'password' && 'Enter your password to continue'}
                {loginStep === 'setup' && 'Set up two-factor authentication'}
                {loginStep === 'totp' && 'Enter the SMS verification code'}
              </p>
            </div>
            
            {/* Password Step */}
            {loginStep === 'password' && (
              <form onSubmit={handlePasswordSubmit} className="space-y-4">
                <Input
                  type="password"
                  placeholder="Enter master password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="bg-slate-950 border-slate-700 text-white"
                  disabled={loginLoading}
                />
                <Button 
                  type="submit" 
                  className="w-full bg-cyan-500 hover:bg-cyan-600"
                  disabled={loginLoading}
                >
                  {loginLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : null}
                  Continue
                </Button>
              </form>
            )}
            
            {/* TOTP Setup Step */}
            {loginStep === 'setup' && (
              <div className="space-y-4">
                <div className="bg-slate-800 rounded-lg p-4 text-center">
                  <p className="text-slate-300 text-sm mb-4">
                    Scan this QR code with your authenticator app:
                  </p>
                  {qrCode && (
                    <img 
                      src={qrCode} 
                      alt="TOTP QR Code" 
                      className="mx-auto rounded-lg mb-4"
                      style={{ maxWidth: '200px' }}
                    />
                  )}
                  <div className="text-xs text-slate-500 mb-2">
                    Or enter this key manually:
                  </div>
                  <code className="text-cyan-400 text-sm bg-slate-900 px-3 py-1 rounded break-all">
                    {secretKey}
                  </code>
                </div>
                
                <div className="space-y-2">
                  <label className="text-slate-300 text-sm">
                    Enter the 6-digit code from your app:
                  </label>
                  <Input
                    type="text"
                    placeholder="000000"
                    value={totpCode}
                    onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    className="bg-slate-950 border-slate-700 text-white text-center text-2xl tracking-widest"
                    maxLength={6}
                    disabled={loginLoading}
                  />
                </div>
                
                <Button 
                  onClick={handleSetupComplete}
                  className="w-full bg-cyan-500 hover:bg-cyan-600"
                  disabled={loginLoading || totpCode.length !== 6}
                >
                  {loginLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : null}
                  Complete Setup
                </Button>
                
                <Button 
                  variant="ghost" 
                  className="w-full text-slate-400"
                  onClick={() => {
                    setLoginStep('password');
                    setQrCode('');
                    setSecretKey('');
                    setTotpCode('');
                  }}
                >
                  Back
                </Button>
              </div>
            )}
            
            {/* TOTP Entry Step */}
            {loginStep === 'totp' && (
              <form onSubmit={handleTotpSubmit} className="space-y-4">
                <div className="bg-slate-800 rounded-lg p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-cyan-500/20 rounded-full flex items-center justify-center">
                      <Smartphone className="w-5 h-5 text-cyan-400" />
                    </div>
                    <div>
                      <p className="text-white text-sm font-medium">Code sent via SMS</p>
                      <p className="text-slate-400 text-xs">Check your phone for the 6-digit code</p>
                    </div>
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="text-slate-300 text-sm">
                    Enter the 6-digit verification code:
                  </label>
                  <Input
                    type="text"
                    placeholder="000000"
                    value={totpCode}
                    onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    className="bg-slate-950 border-slate-700 text-white text-center text-2xl tracking-widest"
                    maxLength={6}
                    autoFocus
                    disabled={loginLoading}
                  />
                </div>
                
                <Button 
                  type="submit"
                  className="w-full bg-cyan-500 hover:bg-cyan-600"
                  disabled={loginLoading || totpCode.length !== 6}
                >
                  {loginLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : null}
                  Verify & Login
                </Button>
                
                <Button 
                  variant="ghost" 
                  className="w-full text-slate-400"
                  onClick={() => {
                    setLoginStep('password');
                    setTotpCode('');
                  }}
                >
                  Back
                </Button>
              </form>
            )}
            
            <div className="mt-4 text-center">
              <a href="/" className="text-cyan-400 hover:text-cyan-300 text-sm">
                Back to Home
              </a>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  // Main Admin Dashboard
  return (
    <div className="min-h-screen bg-slate-950 p-4 md:p-8">
      <WhatsNew />
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold text-white">Master Admin Panel</h1>
            <p className="text-slate-400">Manage all slideshow pages <span className="text-cyan-400 text-xs ml-2">Snowman 1</span></p>
            <p className="text-slate-500 text-xs mt-1">What's New: Full database backup/restore, weather zip code config, display naming in master admin</p>
          </div>
          <div className="flex items-center gap-3">
            <Button 
              onClick={handleRefreshAll}
              variant="outline"
              className="border-cyan-500 text-cyan-400 hover:bg-cyan-500/20"
              disabled={refreshing.all}
            >
              {refreshing.all ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />}
              Refresh All Displays
            </Button>
            <div className="flex items-center gap-2 text-green-400 text-sm">
              <Shield className="w-4 h-4" />
              2FA Active
            </div>
            <Button onClick={handleLogout} variant="outline" className="border-slate-700 text-slate-300">
              Logout
            </Button>
          </div>
        </div>
        
        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-slate-800 pb-2">
          <Button 
            variant={activeTab === 'content' ? 'default' : 'ghost'}
            className={activeTab === 'content' ? 'bg-cyan-500 hover:bg-cyan-600' : 'text-slate-400 hover:text-white'}
            onClick={() => setActiveTab('content')}
          >
            <Monitor className="w-4 h-4 mr-2" /> Pages & Content
          </Button>
          <Button 
            variant={activeTab === 'health' ? 'default' : 'ghost'}
            className={activeTab === 'health' ? 'bg-cyan-500 hover:bg-cyan-600' : 'text-slate-400 hover:text-white'}
            onClick={() => setActiveTab('health')}
          >
            <Activity className="w-4 h-4 mr-2" /> Display Health
          </Button>
          <Button 
            variant={activeTab === 'backup' ? 'default' : 'ghost'}
            className={activeTab === 'backup' ? 'bg-cyan-500 hover:bg-cyan-600' : 'text-slate-400 hover:text-white'}
            onClick={() => setActiveTab('backup')}
          >
            <Download className="w-4 h-4 mr-2" /> Backup
          </Button>
          <Button 
            variant={activeTab === 'tickets' ? 'default' : 'ghost'}
            className={activeTab === 'tickets' ? 'bg-cyan-500 hover:bg-cyan-600' : 'text-slate-400 hover:text-white'}
            onClick={() => setActiveTab('tickets')}
            data-testid="tickets-tab-btn"
          >
            <Ticket className="w-4 h-4 mr-2" /> Support Tickets
          </Button>
        </div>
        
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-cyan-400" />
          </div>
        ) : (
          <>
            {/* Content Tab */}
            {activeTab === 'content' && (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Pages List */}
                <div className="lg:col-span-1">
                  <Card className="bg-slate-900 border-slate-800">
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-white">Pages</CardTitle>
                        <Button
                          size="sm"
                          onClick={() => setShowCreatePage(true)}
                          className="bg-cyan-500 hover:bg-cyan-600"
                        >
                          <Plus className="w-4 h-4 mr-1" /> New Page
                        </Button>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {/* Create New Page Form */}
                      {showCreatePage && (
                        <div className="p-4 bg-slate-800 rounded-lg border border-cyan-500/50 mb-4">
                          <h4 className="text-white font-medium mb-3">Create New Page</h4>
                          <div className="space-y-3">
                            <div>
                              <label className="text-slate-400 text-xs block mb-1">Page ID (URL slug)</label>
                              <Input
                                type="text"
                                placeholder="e.g., ace, lobby, reception"
                                value={newPageId}
                                onChange={(e) => setNewPageId(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                                className="bg-slate-900 border-slate-700 text-white"
                              />
                              <p className="text-slate-500 text-xs mt-1">URL: /page/{newPageId || 'your-id'}</p>
                            </div>
                            <div>
                              <label className="text-slate-400 text-xs block mb-1">Display Name</label>
                              <Input
                                type="text"
                                placeholder="e.g., ACE Display, Lobby Screen"
                                value={newPageName}
                                onChange={(e) => setNewPageName(e.target.value)}
                                className="bg-slate-900 border-slate-700 text-white"
                              />
                            </div>
                            <div>
                              <label className="text-slate-400 text-xs block mb-1">Admin Password</label>
                              <Input
                                type="text"
                                placeholder="Enter admin password"
                                value={newPagePassword}
                                onChange={(e) => setNewPagePassword(e.target.value)}
                                className="bg-slate-900 border-slate-700 text-white"
                              />
                            </div>
                            <div className="flex gap-2 pt-2">
                              <Button
                                onClick={handleCreatePage}
                                disabled={creatingPage}
                                className="bg-cyan-500 hover:bg-cyan-600 flex-1"
                              >
                                {creatingPage ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                                Create
                              </Button>
                              <Button
                                variant="outline"
                                onClick={() => {
                                  setShowCreatePage(false);
                                  setNewPageId('');
                                  setNewPageName('');
                                  setNewPagePassword('');
                                }}
                                className="border-slate-700 text-slate-300"
                              >
                                Cancel
                              </Button>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {pages.map((page) => (
                        <div
                          key={page.id}
                          className={`p-4 rounded-lg cursor-pointer transition-all ${
                            selectedPage?.id === page.id
                              ? 'bg-cyan-500/20 border border-cyan-500'
                              : 'bg-slate-800 hover:bg-slate-700 border border-transparent'
                          }`}
                          onClick={() => {
                            setSelectedPage(page);
                            setWeatherZipInput(page.weather_zip_code || '');
                            setSlideDurationInput(page.slide_duration || 5);
                            setShowDefaultAdInput(page.show_default_ad !== false);
                          }}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <h3 className="text-white font-medium">{page.name}</h3>
                              <p className="text-slate-400 text-sm">{page.route}</p>
                            </div>
                            <div className="flex items-center gap-2">
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-8 w-8 p-0 text-slate-400 hover:text-cyan-400"
                                onClick={(e) => { e.stopPropagation(); handleRefreshPage(page.id); }}
                                disabled={refreshing[page.id]}
                              >
                                {refreshing[page.id] ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                              </Button>
                              {/* Delete button - only for non-default pages */}
                              {!['test', 'dlcoc', 'bkrmmn', 'pcoc'].includes(page.id) && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-8 w-8 p-0 text-slate-400 hover:text-red-400"
                                  onClick={(e) => { e.stopPropagation(); handleDeletePage(page.id); }}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              )}
                              {page.enabled ? (
                                <Eye className="w-4 h-4 text-green-400" />
                              ) : (
                                <EyeOff className="w-4 h-4 text-red-400" />
                              )}
                              <span className="text-slate-400 text-sm">
                                {(page.items || []).length} items
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                </div>
                
                {/* Page Editor */}
                <div className="lg:col-span-2">
                  {selectedPage ? (
                    <Card className="bg-slate-900 border-slate-800">
                      <CardHeader>
                        <div className="flex items-center justify-between">
                          <div>
                            <CardTitle className="text-white">{selectedPage.name}</CardTitle>
                            <div className="flex items-center gap-4 mt-2">
                              <a
                                href={selectedPage.route}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-cyan-400 hover:text-cyan-300 text-sm flex items-center gap-1"
                              >
                                View Page <ExternalLink className="w-3 h-3" />
                              </a>
                              <a
                                href={selectedPage.admin_route}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-slate-400 hover:text-slate-300 text-sm flex items-center gap-1"
                              >
                                Page Admin <ExternalLink className="w-3 h-3" />
                              </a>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className="text-slate-400 text-sm">
                              {selectedPage.enabled ? 'Enabled' : 'Disabled'}
                            </span>
                            <Switch
                              checked={selectedPage.enabled}
                              onCheckedChange={(checked) => togglePageEnabled(selectedPage.id, checked)}
                            />
                          </div>
                        </div>
                        
                        {/* Sub-tabs for Page */}
                        <div className="flex gap-2 mt-4 border-t border-slate-800 pt-4">
                          <Button 
                            size="sm"
                            variant={!editingPlaylist ? 'default' : 'ghost'}
                            className={!editingPlaylist ? 'bg-slate-700' : 'text-slate-400'}
                            onClick={() => setEditingPlaylist(null)}
                          >
                            Content
                          </Button>
                          <Button 
                            size="sm"
                            variant="ghost"
                            className="text-slate-400"
                            onClick={() => {
                              setEditingPlaylist({ items: [] });
                              setPlaylistName('');
                              setPlaylistStartHour(0);
                              setPlaylistEndHour(24);
                            }}
                          >
                            <Clock className="w-4 h-4 mr-1" /> Playlists
                          </Button>
                        </div>
                      </CardHeader>
                      <CardContent className="space-y-6">
                        {/* Playlist Mode Toggle */}
                        <div className="p-4 bg-slate-800 rounded-lg">
                          <div className="flex items-center justify-between">
                            <div>
                              <h4 className="text-white font-medium flex items-center gap-2">
                                <Clock className="w-4 h-4" /> Time-Based Playlists
                              </h4>
                              <p className="text-slate-400 text-sm mt-1">
                                Schedule different content for different times of day
                              </p>
                            </div>
                            <Switch
                              checked={selectedPage.use_playlists || false}
                              onCheckedChange={(checked) => toggleUsePlaylists(selectedPage.id, checked)}
                            />
                          </div>
                          
                          {/* Playlists List */}
                          {selectedPage.use_playlists && (
                            <div className="mt-4 space-y-2">
                              <div className="flex items-center justify-between mb-2">
                                <span className="text-slate-300 text-sm">Configured Playlists:</span>
                                <Button 
                                  size="sm" 
                                  variant="outline" 
                                  className="border-cyan-500 text-cyan-400"
                                  onClick={() => {
                                    setEditingPlaylist({ items: [], isNew: true });
                                    setPlaylistName('');
                                    setPlaylistStartHour(0);
                                    setPlaylistEndHour(24);
                                  }}
                                >
                                  <Plus className="w-4 h-4 mr-1" /> Add Playlist
                                </Button>
                              </div>
                              
                              {(selectedPage.playlists || []).length === 0 ? (
                                <p className="text-slate-500 text-sm">No playlists configured. Add one to get started.</p>
                              ) : (
                                <div className="space-y-2">
                                  {(selectedPage.playlists || []).map((playlist, index) => (
                                    <div key={index} className="flex items-center justify-between bg-slate-900 p-3 rounded-lg">
                                      <div>
                                        <span className="text-white font-medium">{playlist.name}</span>
                                        <span className="text-slate-400 text-sm ml-2">
                                          {playlist.start_hour}:00 - {playlist.end_hour}:00 UTC
                                        </span>
                                        <span className="text-slate-500 text-sm ml-2">
                                          ({playlist.items?.length || 0} items)
                                        </span>
                                      </div>
                                      <div className="flex items-center gap-2">
                                        <Button 
                                          size="sm" 
                                          variant="ghost" 
                                          className="text-cyan-400"
                                          onClick={() => startEditPlaylist(playlist, index)}
                                        >
                                          Edit
                                        </Button>
                                        <Button 
                                          size="sm" 
                                          variant="ghost" 
                                          className="text-red-400"
                                          onClick={() => deletePlaylist(index)}
                                        >
                                          <Trash2 className="w-4 h-4" />
                                        </Button>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                              
                              {/* Playlist Editor */}
                              {editingPlaylist && (
                                <div className="mt-4 p-4 bg-slate-950 rounded-lg border border-slate-700">
                                  <h5 className="text-white font-medium mb-3">
                                    {editingPlaylist.isNew ? 'New Playlist' : 'Edit Playlist'}
                                  </h5>
                                  <div className="grid grid-cols-3 gap-3 mb-3">
                                    <Input
                                      placeholder="Playlist name"
                                      value={playlistName}
                                      onChange={(e) => setPlaylistName(e.target.value)}
                                      className="bg-slate-900 border-slate-700 text-white"
                                    />
                                    <div className="flex items-center gap-2">
                                      <span className="text-slate-400 text-sm">From:</span>
                                      <select 
                                        value={playlistStartHour} 
                                        onChange={(e) => setPlaylistStartHour(e.target.value)}
                                        className="bg-slate-900 border border-slate-700 text-white rounded-md px-2 py-1 flex-1"
                                      >
                                        {[...Array(24)].map((_, i) => (
                                          <option key={i} value={i}>{i}:00</option>
                                        ))}
                                      </select>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <span className="text-slate-400 text-sm">To:</span>
                                      <select 
                                        value={playlistEndHour} 
                                        onChange={(e) => setPlaylistEndHour(e.target.value)}
                                        className="bg-slate-900 border border-slate-700 text-white rounded-md px-2 py-1 flex-1"
                                      >
                                        {[...Array(25)].map((_, i) => (
                                          <option key={i} value={i}>{i}:00</option>
                                        ))}
                                      </select>
                                    </div>
                                  </div>
                                  <p className="text-slate-500 text-xs mb-3">
                                    Items in this playlist will play during the specified hours (UTC time).
                                  </p>
                                  <div className="flex justify-end gap-2">
                                    <Button 
                                      variant="ghost" 
                                      className="text-slate-400"
                                      onClick={() => setEditingPlaylist(null)}
                                    >
                                      Cancel
                                    </Button>
                                    <Button 
                                      className="bg-cyan-500 hover:bg-cyan-600"
                                      onClick={savePlaylist}
                                      disabled={!playlistName.trim()}
                                    >
                                      Save Playlist
                                    </Button>
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                        
                        {/* Orientation Section */}
                        <div className="p-4 bg-slate-800 rounded-lg">
                          <h4 className="text-white font-medium mb-3 flex items-center gap-2">
                            <Monitor className="w-4 h-4" /> Display Orientation
                          </h4>
                          <div className="flex gap-3">
                            <Button
                              variant={selectedPage.orientation === 'landscape' ? 'default' : 'outline'}
                              className={selectedPage.orientation === 'landscape' 
                                ? 'bg-cyan-500 hover:bg-cyan-600' 
                                : 'border-slate-600 text-slate-300 hover:bg-slate-700'}
                              onClick={() => toggleOrientation(selectedPage.id, 'landscape')}
                            >
                              <Monitor className="w-4 h-4 mr-2" />
                              Landscape
                            </Button>
                            <Button
                              variant={selectedPage.orientation === 'portrait' ? 'default' : 'outline'}
                              className={selectedPage.orientation === 'portrait' 
                                ? 'bg-cyan-500 hover:bg-cyan-600' 
                                : 'border-slate-600 text-slate-300 hover:bg-slate-700'}
                              onClick={() => toggleOrientation(selectedPage.id, 'portrait')}
                            >
                              <Smartphone className="w-4 h-4 mr-2" />
                              Portrait
                            </Button>
                          </div>
                        </div>
                        
                        {/* Weather Zip Code Section */}
                        <div className="p-4 bg-slate-800 rounded-lg">
                          <h4 className="text-white font-medium mb-2 flex items-center gap-2">
                            <Cloud className="w-4 h-4" /> Weather Location
                          </h4>
                          <p className="text-slate-400 text-sm mb-3">
                            Set a zip code for the weather slide. Leave empty to auto-detect location.
                          </p>
                          <div className="flex gap-2">
                            <Input
                              type="text"
                              value={weatherZipInput}
                              onChange={(e) => setWeatherZipInput(e.target.value.replace(/\D/g, '').slice(0, 5))}
                              placeholder="e.g., 55401"
                              className="bg-slate-900 border-slate-700 text-white"
                              maxLength={5}
                              data-testid="weather-zip-input"
                            />
                            <Button
                              onClick={() => updateWeatherZipCode(selectedPage.id, weatherZipInput)}
                              className="bg-cyan-500 hover:bg-cyan-600"
                              data-testid="save-weather-zip-btn"
                            >
                              Save
                            </Button>
                            {selectedPage.weather_zip_code && (
                              <Button
                                onClick={() => {
                                  setWeatherZipInput('');
                                  updateWeatherZipCode(selectedPage.id, '');
                                }}
                                variant="outline"
                                className="border-slate-700 text-slate-300 hover:bg-slate-800"
                              >
                                Clear
                              </Button>
                            )}
                          </div>
                          {selectedPage.weather_zip_code && (
                            <p className="text-cyan-400 text-xs mt-2">
                              Currently set to: {selectedPage.weather_zip_code}
                            </p>
                          )}
                        </div>
                        
                        {/* Slide Timing Section */}
                        <div className="p-4 bg-slate-800 rounded-lg">
                          <h4 className="text-white font-medium mb-2 flex items-center gap-2">
                            <Clock className="w-4 h-4" /> Slide Timing
                          </h4>
                          <p className="text-slate-400 text-sm mb-3">
                            Set how long each image/weather slide is displayed. Videos play to completion.
                          </p>
                          <div className="flex items-center gap-3 mb-3">
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-slate-700 text-slate-300 hover:bg-slate-800 h-9 w-9 p-0"
                              onClick={() => updateSlideDuration(selectedPage.id, slideDurationInput - 1)}
                              disabled={slideDurationInput <= 3}
                            >
                              <Minus className="w-4 h-4" />
                            </Button>
                            <div className="bg-slate-900 border border-slate-700 rounded-md px-4 py-2 min-w-[80px] text-center">
                              <span className="text-white text-lg font-medium">{slideDurationInput}</span>
                              <span className="text-slate-400 text-sm ml-1">sec</span>
                            </div>
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-slate-700 text-slate-300 hover:bg-slate-800 h-9 w-9 p-0"
                              onClick={() => updateSlideDuration(selectedPage.id, slideDurationInput + 1)}
                              disabled={slideDurationInput >= 120}
                            >
                              <Plus className="w-4 h-4" />
                            </Button>
                          </div>
                          <div className="flex gap-2 flex-wrap">
                            {[5, 10, 15, 30, 60].map((preset) => (
                              <Button
                                key={preset}
                                size="sm"
                                variant={slideDurationInput === preset ? 'default' : 'outline'}
                                className={slideDurationInput === preset 
                                  ? 'bg-cyan-500 hover:bg-cyan-600 h-8' 
                                  : 'border-slate-700 text-slate-300 hover:bg-slate-800 h-8'}
                                onClick={() => updateSlideDuration(selectedPage.id, preset)}
                              >
                                {preset}s
                              </Button>
                            ))}
                          </div>
                        </div>
                        
                        {/* FrostByte Ad Toggle */}
                        <div className="p-4 bg-slate-800 rounded-lg">
                          <div className="flex items-center justify-between">
                            <div>
                              <h4 className="text-white font-medium flex items-center gap-2">
                                <ImageIcon className="w-4 h-4" /> FrostByte Ad
                              </h4>
                              <p className="text-slate-400 text-sm mt-1">
                                Show the default FrostByte IT ad in the slideshow.
                              </p>
                            </div>
                            <div className="flex items-center gap-3">
                              <span className={`text-sm ${showDefaultAdInput ? 'text-cyan-400' : 'text-slate-500'}`}>
                                {showDefaultAdInput ? 'Showing' : 'Hidden'}
                              </span>
                              <Switch
                                checked={showDefaultAdInput}
                                onCheckedChange={(val) => toggleDefaultAd(selectedPage.id, val)}
                                data-testid="master-ad-toggle"
                              />
                            </div>
                          </div>
                        </div>
                        
                        {/* Password Section */}
                        <div className="p-4 bg-slate-800 rounded-lg">
                          <h4 className="text-white font-medium mb-2 flex items-center gap-2">
                            <Lock className="w-4 h-4" /> Page Password
                          </h4>
                          <div className="flex gap-2">
                            <Input
                              type="text"
                              value={selectedPage.password}
                              onChange={(e) => setSelectedPage({ ...selectedPage, password: e.target.value })}
                              className="bg-slate-900 border-slate-700 text-white"
                            />
                            <Button
                              onClick={() => updatePagePassword(selectedPage.id, selectedPage.password)}
                              className="bg-cyan-500 hover:bg-cyan-600"
                            >
                              Save
                            </Button>
                          </div>
                        </div>
                        
                        {/* Add Content Section */}
                        <div className="p-4 bg-slate-800 rounded-lg">
                          <h4 className="text-white font-medium mb-3">Add Content</h4>
                          
                          <div className="flex gap-2 mb-3">
                            <select
                              value={newItemType}
                              onChange={(e) => setNewItemType(e.target.value)}
                              className="bg-slate-900 border border-slate-700 text-white rounded-md px-3 py-2"
                            >
                              <option value="image">Image</option>
                              <option value="video">Video</option>
                            </select>
                            <Input
                              type="text"
                              placeholder="Enter URL..."
                              value={newItemUrl}
                              onChange={(e) => setNewItemUrl(e.target.value)}
                              className="bg-slate-900 border-slate-700 text-white flex-1"
                            />
                            <Button onClick={handleAddItem} className="bg-cyan-500 hover:bg-cyan-600">
                              <Plus className="w-4 h-4" />
                            </Button>
                          </div>
                          
                          <div className="flex items-center gap-2">
                            <label className="flex-1">
                              <div className="flex items-center justify-center gap-2 p-3 border-2 border-dashed border-slate-600 rounded-lg cursor-pointer hover:border-cyan-500 transition-colors">
                                {uploading ? (
                                  <Loader2 className="w-5 h-5 animate-spin text-cyan-400" />
                                ) : (
                                  <>
                                    <Upload className="w-5 h-5 text-slate-400" />
                                    <span className="text-slate-400">Upload File</span>
                                  </>
                                )}
                              </div>
                              <input
                                type="file"
                                accept="image/*,video/*"
                                onChange={handleFileUpload}
                                className="hidden"
                                disabled={uploading}
                              />
                            </label>
                          </div>
                        </div>
                        
                        {/* Content List */}
                        <div>
                          <h4 className="text-white font-medium mb-3">
                            Content ({(selectedPage.items || []).length} items)
                          </h4>
                          
                          {(selectedPage.items || []).length === 0 ? (
                            <p className="text-slate-400 text-center py-8">No content added yet</p>
                          ) : (
                            <div className="space-y-3">
                              {(selectedPage.items || []).map((item, index) => (
                                <div
                                  key={index}
                                  className="flex gap-4 p-3 bg-slate-800 rounded-lg"
                                >
                                  {/* Thumbnail */}
                                  <div className="w-32 h-20 flex-shrink-0 bg-slate-700 rounded-lg overflow-hidden">
                                    {item.type === 'image' ? (
                                      <img
                                        src={item.url}
                                        alt={`Item ${index + 1}`}
                                        className="w-full h-full object-cover"
                                      />
                                    ) : item.url?.includes('youtube.com') || item.url?.includes('youtu.be') ? (
                                      <img
                                        src={`https://img.youtube.com/vi/${
                                          item.url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/)?.[1] || ''
                                        }/mqdefault.jpg`}
                                        alt={`Video ${index + 1}`}
                                        className="w-full h-full object-cover"
                                      />
                                    ) : (
                                      <div className="w-full h-full flex items-center justify-center">
                                        <Video className="w-8 h-8 text-slate-500" />
                                      </div>
                                    )}
                                  </div>
                                  
                                  {/* Info */}
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 mb-1">
                                      {item.type === 'image' ? (
                                        <ImageIcon className="w-4 h-4 text-cyan-400" />
                                      ) : (
                                        <Video className="w-4 h-4 text-purple-400" />
                                      )}
                                      <span className="text-white text-sm font-medium">
                                        {item.type === 'image' ? 'Image' : 'Video'} #{index + 1}
                                      </span>
                                    </div>
                                    <p className="text-slate-400 text-xs truncate mb-2" title={item.url}>
                                      {item.url}
                                    </p>
                                    <Input
                                      type="text"
                                      placeholder="Add description (optional)..."
                                      value={item.description || ''}
                                      onChange={(e) => {
                                        const updatedItems = [...selectedPage.items];
                                        updatedItems[index] = { ...item, description: e.target.value };
                                        setSelectedPage({ ...selectedPage, items: updatedItems });
                                      }}
                                      onBlur={() => {
                                        // Auto-save on blur
                                        handleUpdateItemDescription(index, selectedPage.items[index].description);
                                      }}
                                      className="bg-slate-900 border-slate-700 text-white text-xs h-8"
                                    />
                                  </div>
                                  
                                  {/* Actions */}
                                  <div className="flex items-center gap-2">
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      className="text-slate-400 hover:text-red-400"
                                      onClick={() => handleRemoveItem(index)}
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ) : (
                    <Card className="bg-slate-900 border-slate-800">
                      <CardContent className="py-16 text-center">
                        <p className="text-slate-400">Select a page to edit</p>
                      </CardContent>
                    </Card>
                  )}
                </div>
              </div>
            )}
            
            {/* Health Tab */}
            {activeTab === 'health' && (
              <Card className="bg-slate-900 border-slate-800">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-white flex items-center gap-2">
                      <Activity className="w-5 h-5" /> Display Health Monitoring
                    </CardTitle>
                    <Button 
                      onClick={loadDisplayHealth} 
                      variant="outline" 
                      className="border-slate-700 text-slate-300"
                      disabled={healthLoading}
                    >
                      {healthLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                      Refresh
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {/* Fully Kiosk Settings */}
                  <div className="mb-6 p-4 bg-slate-800 rounded-lg border border-slate-700">
                    <h3 className="text-white font-medium mb-3 flex items-center gap-2">
                      <Tv className="w-4 h-4 text-cyan-400" /> Fully Kiosk Browser Settings
                    </h3>
                    <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-end">
                      <div className="flex-1">
                        <label className="text-slate-400 text-xs mb-1 block">Remote Admin Password</label>
                        <Input
                          type="password"
                          value={fullyPassword}
                          onChange={(e) => setFullyPassword(e.target.value)}
                          placeholder="Enter Fully Kiosk remote admin password"
                          className="bg-slate-900 border-slate-700 text-white"
                          data-testid="fully-password-input"
                        />
                      </div>
                      <Button
                        onClick={saveFullySettings}
                        className="bg-cyan-500 hover:bg-cyan-600"
                        data-testid="save-fully-settings-btn"
                      >
                        Save Password
                      </Button>
                    </div>
                    <p className="text-slate-500 text-xs mt-2">
                      This password is set in Fully Kiosk Browser's Remote Administration settings on each device.
                    </p>
                  </div>

                  {/* Bulk Controls */}
                  {displayHealth.length > 0 && (
                    <div className="mb-6 p-4 bg-slate-800 rounded-lg border border-slate-700">
                      <p className="text-slate-400 text-xs mb-2 uppercase tracking-wider font-semibold">All Displays</p>
                      <div className="flex flex-wrap gap-2">
                        <Button size="sm" onClick={() => sendFullyBulkCommand('reload')} disabled={fullySending['bulk-reload']} className="bg-cyan-500 hover:bg-cyan-600 h-8" data-testid="master-bulk-reload">
                          {fullySending['bulk-reload'] ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <RefreshCw className="w-3 h-3 mr-1" />} Reload All
                        </Button>
                        <Button size="sm" onClick={() => sendFullyBulkCommand('screenOn')} disabled={fullySending['bulk-screenOn']} variant="outline" className="border-slate-600 text-slate-300 hover:bg-slate-700 h-8">
                          <Sun className="w-3 h-3 mr-1" /> Screens On
                        </Button>
                        <Button size="sm" onClick={() => sendFullyBulkCommand('screenOff')} disabled={fullySending['bulk-screenOff']} variant="outline" className="border-slate-600 text-slate-300 hover:bg-slate-700 h-8">
                          <Moon className="w-3 h-3 mr-1" /> Screens Off
                        </Button>
                        <Button size="sm" onClick={() => sendFullyBulkCommand('clearCache')} disabled={fullySending['bulk-clearCache']} variant="outline" className="border-slate-600 text-slate-300 hover:bg-slate-700 h-8">
                          <Trash2 className="w-3 h-3 mr-1" /> Clear Cache All
                        </Button>
                        <Button size="sm" onClick={() => sendFullyBulkCommand('restart')} disabled={fullySending['bulk-restart']} variant="outline" className="border-red-500/40 text-red-400 hover:bg-red-500/10 h-8">
                          <RotateCcw className="w-3 h-3 mr-1" /> Restart All
                        </Button>
                      </div>
                    </div>
                  )}
                  {healthLoading && displayHealth.length === 0 ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-cyan-400" />
                    </div>
                  ) : displayHealth.length === 0 ? (
                    <div className="text-center py-8">
                      <WifiOff className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                      <p className="text-slate-400">No displays have checked in yet.</p>
                      <p className="text-slate-500 text-sm mt-2">
                        Displays will appear here once they start reporting their status.
                      </p>
                    </div>
                  ) : (
                    <div className="grid gap-4 md:grid-cols-2">
                      {displayHealth.map((display) => (
                        <div 
                          key={display.display_id} 
                          className={`p-4 rounded-lg border ${
                            display.status === 'online' 
                              ? 'bg-green-500/10 border-green-500/30' 
                              : 'bg-red-500/10 border-red-500/30'
                          }`}
                        >
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2">
                              {display.status === 'online' ? (
                                <Wifi className="w-5 h-5 text-green-400" />
                              ) : (
                                <WifiOff className="w-5 h-5 text-red-400" />
                              )}
                              {/* Editable Display Name */}
                              {editingDisplayId === display.display_id ? (
                                <div className="flex items-center gap-1">
                                  <Input
                                    type="text"
                                    value={editDisplayName}
                                    onChange={(e) => setEditDisplayName(e.target.value)}
                                    placeholder="Enter display name..."
                                    className="bg-slate-900 border-slate-700 text-white text-sm h-7 w-40"
                                    autoFocus
                                    data-testid="edit-display-name-input"
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') saveDisplayName(display.display_id);
                                      if (e.key === 'Escape') { setEditingDisplayId(null); setEditDisplayName(''); }
                                    }}
                                  />
                                  <Button
                                    size="sm"
                                    onClick={() => saveDisplayName(display.display_id)}
                                    className="h-7 w-7 p-0 bg-green-500 hover:bg-green-600"
                                    data-testid="save-display-name-btn"
                                  >
                                    <Check className="w-3 h-3" />
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => { setEditingDisplayId(null); setEditDisplayName(''); }}
                                    className="h-7 w-7 p-0 border-slate-700"
                                  >
                                    <X className="w-3 h-3" />
                                  </Button>
                                </div>
                              ) : (
                                <div className="flex items-center gap-2 cursor-pointer group" onClick={() => startEditingDisplayName(display)}>
                                  <span className={`font-medium ${
                                    display.status === 'online' ? 'text-green-400' : 'text-red-400'
                                  }`}>
                                    {display.display_name || 'Unnamed Display'}
                                  </span>
                                  <Edit2 className="w-3 h-3 text-slate-500 opacity-0 group-hover:opacity-100 transition-opacity" />
                                </div>
                              )}
                            </div>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-slate-400 hover:text-red-400"
                              onClick={() => removeDisplay(display.display_id)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                          
                          {/* Status badge */}
                          <div className="mb-3">
                            <span className={`text-xs px-2 py-0.5 rounded-full ${
                              display.status === 'online' 
                                ? 'bg-green-500/20 text-green-400' 
                                : 'bg-red-500/20 text-red-400'
                            }`}>
                              {display.status === 'online' ? 'Online' : 'Offline'}
                            </span>
                          </div>
                          
                          <div className="grid grid-cols-2 gap-2 text-sm">
                            <div>
                              <span className="text-slate-500">Page:</span>
                              <span className="text-white ml-2">{display.page_id}</span>
                            </div>
                            {display.ip_address && (
                              <div>
                                <span className="text-slate-500">IP:</span>
                                <span className="text-white ml-2">{display.ip_address}</span>
                              </div>
                            )}
                            {display.screen_width && display.screen_height && (
                              <div>
                                <span className="text-slate-500">Resolution:</span>
                                <span className="text-cyan-400 ml-2">{display.screen_width}x{display.screen_height}</span>
                              </div>
                            )}
                            {display.orientation && (
                              <div>
                                <span className="text-slate-500">Orientation:</span>
                                <span className="text-white ml-2">{display.orientation}</span>
                              </div>
                            )}
                            {display.browser && (
                              <div>
                                <span className="text-slate-500">Browser:</span>
                                <span className="text-white ml-2">{display.browser}</span>
                              </div>
                            )}
                            {display.os && (
                              <div>
                                <span className="text-slate-500">OS:</span>
                                <span className="text-white ml-2">{display.os}</span>
                              </div>
                            )}
                            {display.connection_type && display.connection_type !== 'Unknown' && (
                              <div>
                                <span className="text-slate-500">Connection:</span>
                                <span className="text-white ml-2">{display.connection_type}</span>
                              </div>
                            )}
                            {display.total_items !== undefined && (
                              <div>
                                <span className="text-slate-500">Playing:</span>
                                <span className="text-cyan-400 ml-2">
                                  {(display.current_item_index || 0) + 1} / {display.total_items} items
                                </span>
                              </div>
                            )}
                          </div>
                          
                          <div className="mt-3 pt-3 border-t border-slate-700">
                            <span className="text-slate-500 text-xs">Last seen: </span>
                            <span className="text-slate-300 text-xs">
                              {new Date(display.last_seen).toLocaleString()}
                            </span>
                          </div>
                          
                          {/* Per-display Fully Kiosk controls */}
                          <div className="mt-3 pt-3 border-t border-slate-700">
                            <div className="flex flex-wrap gap-1.5">
                              <Button size="sm" onClick={() => sendFullyCommand(display.display_id, 'reload')} disabled={fullySending[`${display.display_id}-reload`]} className="bg-cyan-500/20 text-cyan-400 hover:bg-cyan-500/30 border border-cyan-500/30 h-6 text-xs px-2">
                                {fullySending[`${display.display_id}-reload`] ? <Loader2 className="w-3 h-3 animate-spin" /> : <><RefreshCw className="w-3 h-3 mr-1" />Reload</>}
                              </Button>
                              <Button size="sm" onClick={() => sendFullyCommand(display.display_id, 'screenOn')} variant="outline" className="border-slate-600 text-slate-300 hover:bg-slate-700 h-6 text-xs px-2">
                                <Sun className="w-3 h-3 mr-1" />On
                              </Button>
                              <Button size="sm" onClick={() => sendFullyCommand(display.display_id, 'screenOff')} variant="outline" className="border-slate-600 text-slate-300 hover:bg-slate-700 h-6 text-xs px-2">
                                <Moon className="w-3 h-3 mr-1" />Off
                              </Button>
                              <Button size="sm" onClick={() => sendFullyCommand(display.display_id, 'restart')} variant="outline" className="border-red-500/40 text-red-400 hover:bg-red-500/10 h-6 text-xs px-2">
                                <RotateCcw className="w-3 h-3 mr-1" />Restart
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
            
            {/* Backup Tab */}
            {activeTab === 'backup' && (
              <Card className="bg-slate-900 border-slate-800">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Download className="w-5 h-5" /> Backup & Restore
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-2 gap-6">
                    {/* Export */}
                    <div className="p-6 bg-slate-800 rounded-lg">
                      <h3 className="text-white font-medium mb-2 flex items-center gap-2">
                        <Download className="w-5 h-5 text-cyan-400" /> Export Full Backup
                      </h3>
                      <p className="text-slate-400 text-sm mb-4">
                        Download a full database backup including all pages, content, contacts, displays, and playlists as a JSON file.
                      </p>
                      <Button 
                        onClick={handleExportBackup}
                        className="bg-cyan-500 hover:bg-cyan-600 w-full"
                        data-testid="export-backup-btn"
                      >
                        <Download className="w-4 h-4 mr-2" /> Export Full Database Backup
                      </Button>
                    </div>
                    
                    {/* Import */}
                    <div className="p-6 bg-slate-800 rounded-lg">
                      <h3 className="text-white font-medium mb-2 flex items-center gap-2">
                        <UploadIcon className="w-5 h-5 text-cyan-400" /> Import Backup
                      </h3>
                      <p className="text-slate-400 text-sm mb-4">
                        Restore page configurations from a previously exported JSON file.
                      </p>
                      <label className="block">
                        <div className="flex items-center justify-center gap-2 p-3 border-2 border-dashed border-slate-600 rounded-lg cursor-pointer hover:border-cyan-500 transition-colors">
                          <UploadIcon className="w-5 h-5 text-slate-400" />
                          <span className="text-slate-400">Select Backup File</span>
                        </div>
                        <input
                          type="file"
                          accept=".json"
                          onChange={handleImportBackup}
                          className="hidden"
                        />
                      </label>
                    </div>
                  </div>
                  
                  {/* Update Notification */}
                  <div className="mt-6 p-6 bg-slate-800 rounded-lg">
                    <h3 className="text-white font-medium mb-2 flex items-center gap-2">
                      <Mail className="w-5 h-5 text-cyan-400" /> Send Update Notification
                    </h3>
                    <p className="text-slate-400 text-sm mb-4">
                      Notify stakeholders about this deployment. Emails will be sent to dsupdates@frostbyteitmn.com and kevin@fundtech.llc
                    </p>
                    <textarea
                      value={notificationChanges}
                      onChange={(e) => setNotificationChanges(e.target.value)}
                      placeholder="Enter changes (one per line):&#10;- Added weather slide&#10;- Fixed image loading&#10;- Updated to Snowman 1"
                      className="w-full h-32 bg-slate-900 border border-slate-700 text-white rounded-lg p-3 mb-4 placeholder:text-slate-500"
                    />
                    <Button 
                      onClick={handleSendNotification}
                      disabled={sendingNotification || !notificationChanges.trim()}
                      className="bg-cyan-500 hover:bg-cyan-600"
                    >
                      {sendingNotification ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Send className="w-4 h-4 mr-2" />
                      )}
                      Send Notification (Snowman 1)
                    </Button>
                  </div>
                  
                  <div className="mt-6 p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                    <p className="text-yellow-400 text-sm">
                      <strong>Note:</strong> Importing a backup will overwrite existing page configurations with matching IDs. 
                      Make sure to export your current configuration first if you want to preserve it.
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Tickets Tab */}
            {activeTab === 'tickets' && (
              <Card className="bg-slate-900 border-slate-800">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-white flex items-center gap-2">
                      <Ticket className="w-5 h-5" /> Support Tickets
                    </CardTitle>
                    <Button
                      onClick={loadTickets}
                      variant="outline"
                      className="border-slate-700 text-slate-300 hover:bg-slate-800"
                      disabled={ticketsLoading}
                      data-testid="refresh-tickets-btn"
                    >
                      {ticketsLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                      Refresh
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {ticketsLoading && tickets.length === 0 ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="w-6 h-6 animate-spin text-cyan-400" />
                    </div>
                  ) : tickets.length === 0 ? (
                    <div className="text-center py-12">
                      <Ticket className="w-10 h-10 text-slate-600 mx-auto mb-3" />
                      <p className="text-slate-400">No support tickets yet.</p>
                      <p className="text-slate-500 text-sm mt-1">Tickets submitted by customers will appear here.</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {/* Stats bar */}
                      <div className="flex gap-4 mb-4 p-3 bg-slate-800 rounded-lg text-sm">
                        <span className="text-slate-300">
                          Total: <span className="text-white font-medium">{tickets.length}</span>
                        </span>
                        <span className="text-cyan-400">
                          Open: {tickets.filter(t => t.status === 'open').length}
                        </span>
                        <span className="text-amber-400">
                          In Progress: {tickets.filter(t => t.status === 'in_progress').length}
                        </span>
                        <span className="text-emerald-400">
                          Resolved: {tickets.filter(t => t.status === 'resolved').length}
                        </span>
                        <span className="text-slate-500">
                          Closed: {tickets.filter(t => t.status === 'closed').length}
                        </span>
                      </div>

                      {tickets.map((ticket) => (
                        <div
                          key={ticket.ticket_id}
                          data-testid={`admin-ticket-${ticket.ticket_id}`}
                          className="bg-slate-800 border border-slate-700 rounded-lg p-4"
                        >
                          {editingTicketId === ticket.ticket_id ? (
                            // Editing mode
                            <div className="space-y-3">
                              <div className="flex items-center justify-between">
                                <span className="text-cyan-400 font-mono font-semibold">{ticket.ticket_id}</span>
                                <div className="flex gap-2">
                                  <Button size="sm" className="bg-cyan-500 hover:bg-cyan-600 h-8" onClick={() => saveTicketUpdate(ticket.ticket_id)} data-testid={`save-ticket-${ticket.ticket_id}`}>
                                    <Check className="w-3 h-3 mr-1" /> Save
                                  </Button>
                                  <Button size="sm" variant="ghost" className="text-slate-400 h-8" onClick={cancelEditTicket}>
                                    <X className="w-3 h-3 mr-1" /> Cancel
                                  </Button>
                                </div>
                              </div>
                              <div>
                                <label className="text-slate-400 text-xs block mb-1">Status</label>
                                <select
                                  value={editTicketStatus}
                                  onChange={e => setEditTicketStatus(e.target.value)}
                                  className="bg-slate-900 border border-slate-700 text-white rounded-md px-3 py-2 w-full"
                                  data-testid={`status-select-${ticket.ticket_id}`}
                                >
                                  <option value="open">Open</option>
                                  <option value="in_progress">In Progress</option>
                                  <option value="resolved">Resolved</option>
                                  <option value="closed">Closed</option>
                                </select>
                              </div>
                              <div>
                                <label className="text-slate-400 text-xs block mb-1">Tech Notes</label>
                                <textarea
                                  value={editTicketNotes}
                                  onChange={e => setEditTicketNotes(e.target.value)}
                                  rows={3}
                                  placeholder="Add notes about the repair..."
                                  className="w-full bg-slate-900 border border-slate-700 text-white rounded-lg p-3 text-sm placeholder:text-slate-500 resize-none focus:border-cyan-500 focus:outline-none"
                                  data-testid={`notes-input-${ticket.ticket_id}`}
                                />
                              </div>
                              <div className="grid grid-cols-2 gap-3">
                                <div>
                                  <label className="text-slate-400 text-xs block mb-1">Device Model</label>
                                  <Input
                                    value={editTicketModel}
                                    onChange={e => setEditTicketModel(e.target.value)}
                                    placeholder="e.g., Dell Inspiron 15"
                                    className="bg-slate-900 border-slate-700 text-white placeholder:text-slate-500"
                                    data-testid={`model-input-${ticket.ticket_id}`}
                                  />
                                </div>
                                <div>
                                  <label className="text-slate-400 text-xs block mb-1">Serial Number</label>
                                  <Input
                                    value={editTicketSerial}
                                    onChange={e => setEditTicketSerial(e.target.value)}
                                    placeholder="e.g., SN-12345678"
                                    className="bg-slate-900 border-slate-700 text-white placeholder:text-slate-500"
                                    data-testid={`serial-input-${ticket.ticket_id}`}
                                  />
                                </div>
                              </div>
                            </div>
                          ) : (
                            // Display mode
                            <div>
                              <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center gap-3">
                                  <span className="text-cyan-400 font-mono font-semibold text-sm">{ticket.ticket_id}</span>
                                  <span className={`px-2 py-0.5 rounded-full border text-xs font-medium ${
                                    ticket.status === 'open' ? 'text-cyan-400 bg-cyan-400/10 border-cyan-400/30' :
                                    ticket.status === 'in_progress' ? 'text-amber-400 bg-amber-400/10 border-amber-400/30' :
                                    ticket.status === 'resolved' ? 'text-emerald-400 bg-emerald-400/10 border-emerald-400/30' :
                                    'text-slate-400 bg-slate-400/10 border-slate-400/30'
                                  }`}>
                                    {ticket.status === 'open' ? 'Open' : ticket.status === 'in_progress' ? 'In Progress' : ticket.status === 'resolved' ? 'Resolved' : 'Closed'}
                                  </span>
                                </div>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="text-slate-400 hover:text-cyan-400 h-8"
                                  onClick={() => startEditTicket(ticket)}
                                  data-testid={`edit-ticket-${ticket.ticket_id}`}
                                >
                                  <Edit2 className="w-3 h-3 mr-1" /> Edit
                                </Button>
                              </div>
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm mb-2">
                                <div>
                                  <span className="text-slate-500 text-xs block">Customer</span>
                                  <span className="text-white">{ticket.customer_name}</span>
                                </div>
                                <div>
                                  <span className="text-slate-500 text-xs block">Device</span>
                                  <span className="text-white">{ticket.device_type}</span>
                                </div>
                                <div>
                                  <span className="text-slate-500 text-xs block">Phone</span>
                                  <span className="text-white">{ticket.phone_number}</span>
                                </div>
                                <div>
                                  <span className="text-slate-500 text-xs block">Created</span>
                                  <span className="text-white">{new Date(ticket.created_at).toLocaleDateString()}</span>
                                </div>
                              </div>
                              <div className="mt-2">
                                <span className="text-slate-500 text-xs block mb-1">Issue</span>
                                <p className="text-slate-300 text-sm bg-slate-900/50 rounded p-2">{ticket.issue_description}</p>
                              </div>
                              {(ticket.device_model || ticket.serial_number) && (
                                <div className="mt-2 grid grid-cols-2 gap-3 text-sm">
                                  {ticket.device_model && (
                                    <div>
                                      <span className="text-slate-500 text-xs block">Model</span>
                                      <span className="text-white">{ticket.device_model}</span>
                                    </div>
                                  )}
                                  {ticket.serial_number && (
                                    <div>
                                      <span className="text-slate-500 text-xs block">Serial #</span>
                                      <span className="text-white font-mono text-xs">{ticket.serial_number}</span>
                                    </div>
                                  )}
                                </div>
                              )}
                              {ticket.customer_updates && ticket.customer_updates.length > 0 && (
                                <div className="mt-2">
                                  <span className="text-slate-500 text-xs block mb-1">Customer Updates ({ticket.customer_updates.length})</span>
                                  <div className="space-y-1">
                                    {ticket.customer_updates.map((u, i) => (
                                      <div key={i} className="text-sm bg-blue-400/5 border border-blue-400/10 rounded p-2">
                                        <p className="text-blue-300">{u.message}</p>
                                        <p className="text-slate-500 text-xs mt-1">{new Date(u.timestamp).toLocaleString()}</p>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                              {ticket.admin_notes && (
                                <div className="mt-2">
                                  <span className="text-slate-500 text-xs block mb-1 flex items-center gap-1">
                                    <MessageSquare className="w-3 h-3" /> Tech Notes
                                  </span>
                                  <p className="text-emerald-300 text-sm bg-emerald-400/5 border border-emerald-400/10 rounded p-2">{ticket.admin_notes}</p>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default MasterAdmin;
